import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import {
  archiveManufacturingPurchaseOrder,
  deleteManufacturingPurchaseOrder,
} from "@/lib/manufacturing/purchaseOrderClient";
import { canHardDeleteManufacturingPurchaseOrder } from "@/lib/manufacturing/purchaseOrderDeleteAccess";
import type { MfgPurchaseOrder } from "@/lib/manufacturing/purchaseOrderTypes";
import {
  filterPurchaseOrderQueue,
  purchaseOrderQueueHint,
  type PurchaseOrderQueueFilter,
} from "@/lib/manufacturing/purchaseOrderQueue";
import { plantRequestInboxBlurb } from "@/lib/manufacturing/plantMaterialRequest";
import { ghostBtnClass, inputClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import { PurchaseOrderArchiveDialog } from "@/components/manufacturing/PurchaseOrderArchiveDialog";

const FILTERS: Array<{ id: PurchaseOrderQueueFilter; label: string }> = [
  { id: "open", label: "Open" },
  { id: "requests", label: "Requests" },
  { id: "needs_invoice", label: "Needs invoice" },
  { id: "needs_delivery", label: "Needs delivery" },
  { id: "archived", label: "Archived" },
  { id: "all", label: "All" },
];

export function GeneratePurchaseOrderQueue(props: {
  orders: MfgPurchaseOrder[];
  activeId: string;
  busy: boolean;
  onNew: () => void;
  onOpen: (poId: string) => void;
  onArchived: (order: MfgPurchaseOrder) => void;
  onDeleted: (poId: string) => void;
  onError: (text: string) => void;
  initialFilter?: PurchaseOrderQueueFilter;
}) {
  const auth = useAuth();
  const canHardDelete =
    auth?.status === "ready" && canHardDeleteManufacturingPurchaseOrder((...r) => auth.hasRole(...r));
  const [filter, setFilter] = useState<PurchaseOrderQueueFilter>(props.initialFilter ?? "open");
  useEffect(() => {
    if (props.initialFilter) setFilter(props.initialFilter);
  }, [props.initialFilter]);
  const [actingId, setActingId] = useState("");
  const [archiveTarget, setArchiveTarget] = useState<MfgPurchaseOrder | null>(null);
  const rows = useMemo(() => filterPurchaseOrderQueue(props.orders, filter), [filter, props.orders]);
  const counts = useMemo(
    () => ({
      open: filterPurchaseOrderQueue(props.orders, "open").length,
      requests: filterPurchaseOrderQueue(props.orders, "requests").length,
      needs_invoice: filterPurchaseOrderQueue(props.orders, "needs_invoice").length,
      needs_delivery: filterPurchaseOrderQueue(props.orders, "needs_delivery").length,
      archived: filterPurchaseOrderQueue(props.orders, "archived").length,
      all: props.orders.length,
    }),
    [props.orders]
  );
  const locked = props.busy || Boolean(actingId);

  async function archiveOrder(order: MfgPurchaseOrder, reason: string) {
    setActingId(order.id);
    const res = await archiveManufacturingPurchaseOrder({ po_id: order.id, reason });
    setActingId("");
    if (!res.ok) {
      props.onError(res.error);
      return;
    }
    setArchiveTarget(null);
    props.onArchived(res.data.order);
  }

  async function deleteOrder(order: MfgPurchaseOrder) {
    if (
      !window.confirm(
        `Permanently delete PO ${order.po_number}? This cannot be undone.`
      )
    ) {
      return;
    }
    setActingId(order.id);
    const res = await deleteManufacturingPurchaseOrder({ po_id: order.id });
    setActingId("");
    if (!res.ok) {
      props.onError(res.error);
      return;
    }
    props.onDeleted(order.id);
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className={ghostBtnClass} disabled={locked} onClick={props.onNew}>
          New PO
        </button>
        {FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            className={`rounded-full px-2.5 py-1 text-xs ${
              filter === f.id ? "bg-teal-700 text-white" : "bg-gray-100 text-gray-800 dark:bg-zinc-800 dark:text-zinc-100"
            }`}
            onClick={() => setFilter(f.id)}
          >
            {f.label} ({counts[f.id]})
          </button>
        ))}
        <select
          className={`${inputClass} max-w-xs`}
          value={props.activeId}
          disabled={locked}
          onChange={(e) => {
            if (!e.target.value) props.onNew();
            else props.onOpen(e.target.value);
          }}
        >
          <option value="">Jump to PO…</option>
          {props.orders.map((o) => (
            <option key={o.id} value={o.id}>
              {o.po_number} · {o.status === "cancelled" ? "archived" : o.status} · {o.vendor_name || "no vendor"}
            </option>
          ))}
        </select>
      </div>
      {archiveTarget ? (
        <PurchaseOrderArchiveDialog
          poNumber={archiveTarget.po_number}
          busy={actingId === archiveTarget.id}
          onCancel={() => setArchiveTarget(null)}
          onConfirm={(reason) => void archiveOrder(archiveTarget, reason)}
        />
      ) : null}
      <ul className="max-h-36 divide-y overflow-y-auto rounded-md border border-gray-200 text-xs dark:border-zinc-700">
        {rows.map((o) => {
          const active = o.id === props.activeId;
          const rowBusy = locked;
          return (
            <li key={o.id} className="flex items-center gap-1">
              <button
                type="button"
                disabled={rowBusy}
                onClick={() => props.onOpen(o.id)}
                className={`flex min-w-0 flex-1 items-center gap-2 px-2 py-1.5 text-left ${
                  active ? "bg-teal-50 dark:bg-teal-950/40" : "hover:bg-gray-50 dark:hover:bg-zinc-800"
                }`}
              >
                <span className="font-medium">{o.po_number}</span>
                <span className="truncate text-gray-600 dark:text-zinc-400">
                  {o.origin === "plant_request"
                    ? plantRequestInboxBlurb(o)
                    : o.vendor_name || "no vendor"}
                </span>
                <span className="ml-auto shrink-0 text-gray-500">{purchaseOrderQueueHint(o)}</span>
              </button>
              {o.status !== "cancelled" ? (
                <button
                  type="button"
                  className="shrink-0 px-1 text-[11px] font-semibold text-gray-600 hover:underline disabled:opacity-50 dark:text-zinc-300"
                  disabled={rowBusy}
                  onClick={() => setArchiveTarget(o)}
                >
                  Archive
                </button>
              ) : null}
              {canHardDelete ? (
                <button
                  type="button"
                  className="shrink-0 px-1 pr-2 text-[11px] font-semibold text-red-600 hover:underline disabled:opacity-50"
                  disabled={rowBusy}
                  onClick={() => void deleteOrder(o)}
                >
                  Delete
                </button>
              ) : null}
            </li>
          );
        })}
        {rows.length === 0 ? (
          <li className="px-2 py-3 text-gray-500">No purchase orders in this filter.</li>
        ) : null}
      </ul>
    </div>
  );
}
